import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminUsers() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // null | 'createAdmin' | 'resetPw'
  const [target,  setTarget]  = useState(null);
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [search,  setSearch]  = useState('');

  useEffect(() => {
    api.get('/admin/users')
      .then(r => setUsers(r.data.data || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = users.filter(u =>
    !search ||
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const createAdmin = async () => {
    if (!form.name || !form.email || !form.password) { toast.error('Name, email and password required.'); return; }
    setSaving(true);
    try {
      const r = await api.post('/admin/create-admin', form);
      setUsers(us => [r.data.data, ...us]);
      toast.success('Admin account created!');
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const resetPassword = async () => {
    if (!form.newPassword || form.newPassword.length < 6) { toast.error('Min 6 characters.'); return; }
    setSaving(true);
    try {
      await api.put(`/admin/reset-password/${target._id}`, { newPassword: form.newPassword });
      toast.success(`Password reset for ${target.name}.`);
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (user) => {
    try {
      const r = await api.put(`/admin/users/${user._id}`, { isActive: !user.isActive, role: user.role });
      setUsers(us => us.map(u => u._id === user._id ? r.data.data : u));
      toast.success(`User ${!user.isActive ? 'activated' : 'deactivated'}.`);
    } catch { toast.error('Failed.'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-headline font-black text-3xl uppercase tracking-tight">Users</h1>
          <p className="font-body text-sm text-secondary mt-1">{users.length} total users · {users.filter(u => u.role === 'admin').length} admins</p>
        </div>
        <button onClick={() => { setForm({ name: '', email: '', mobile: '', password: '' }); setModal('createAdmin'); }}
          className="btn-primary">
          <span className="material-symbols-outlined">admin_panel_settings</span> Add Admin
        </button>
      </div>

      {/* Search */}
      <div className="mb-5">
        <input value={search} onChange={e => setSearch(e.target.value)}
          className="input-field max-w-sm" placeholder="Search by name or email..." />
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-14 skeleton" />)}</div>
      ) : (
        <div className="bg-white border border-outline-variant/20 overflow-x-auto">
          <table className="cf-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u._id}>
                  <td className="font-bold">{u.name}</td>
                  <td className="text-secondary text-sm">{u.email}</td>
                  <td className="text-secondary text-sm">{u.mobile || '—'}</td>
                  <td>
                    <span className={`badge ${u.role === 'admin' ? 'badge-orange' : 'badge-gray'}`}>{u.role}</span>
                  </td>
                  <td>
                    <span className={`badge ${u.isActive ? 'badge-green' : 'badge-red'}`}>{u.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="text-secondary text-xs">
                    {new Date(u.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => { setTarget(u); setForm({ newPassword: '' }); setModal('resetPw'); }}
                        className="p-1.5 text-secondary hover:text-primary-container transition-colors" title="Reset Password">
                        <span className="material-symbols-outlined text-[18px]">lock_reset</span>
                      </button>
                      <button onClick={() => toggleActive(u)}
                        className={`p-1.5 transition-colors ${u.isActive ? 'text-secondary hover:text-error' : 'text-secondary hover:text-green-600'}`}
                        title={u.isActive ? 'Deactivate' : 'Activate'}>
                        <span className="material-symbols-outlined text-[18px]">{u.isActive ? 'block' : 'check_circle'}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-secondary">No users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Admin Modal */}
      {modal === 'createAdmin' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-container">
              <h2 className="font-headline font-black text-xl uppercase">Add Admin Account</h2>
              <button onClick={() => setModal(null)}><span className="material-symbols-outlined">close</span></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-surface-container-low border-l-4 border-primary-container">
                <p className="font-label text-xs text-secondary">
                  This creates a new admin account with full admin panel access. Use for <strong>centralfasteners.in</strong> email or trusted staff only.
                </p>
              </div>
              <div>
                <label className="input-label">Full Name *</label>
                <input name="name" value={form.name || ''} onChange={h} className="input-field" placeholder="Admin name" />
              </div>
              <div>
                <label className="input-label">Email Address *</label>
                <input name="email" type="email" value={form.email || ''} onChange={h} className="input-field"
                  placeholder="e.g. admin@centralfasteners.in" />
              </div>
              <div>
                <label className="input-label">Mobile</label>
                <input name="mobile" value={form.mobile || ''} onChange={h} className="input-field" placeholder="+91 XXXXX XXXXX" />
              </div>
              <div>
                <label className="input-label">Password *</label>
                <input name="password" type="password" value={form.password || ''} onChange={h} className="input-field" placeholder="Min 6 characters" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-surface-container flex justify-end gap-3">
              <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button onClick={createAdmin} disabled={saving} className="btn-primary">
                {saving ? 'Creating...' : <><span className="material-symbols-outlined">admin_panel_settings</span> Create Admin</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {modal === 'resetPw' && target && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-container">
              <h2 className="font-headline font-black text-xl uppercase">Reset Password</h2>
              <button onClick={() => setModal(null)}><span className="material-symbols-outlined">close</span></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="font-body text-sm text-secondary">
                Resetting password for <span className="font-bold text-on-surface">{target.name}</span> ({target.email})
              </p>
              <div>
                <label className="input-label">New Password *</label>
                <input name="newPassword" type="password" value={form.newPassword || ''} onChange={h}
                  className="input-field" placeholder="Min 6 characters" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-surface-container flex justify-end gap-3">
              <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button onClick={resetPassword} disabled={saving} className="btn-primary">
                {saving ? 'Resetting...' : <><span className="material-symbols-outlined">lock_reset</span> Reset Password</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
